import store from '@/store';

const fieldCache = new WeakMap();

export const CUSTOM_ELEMENT_SELECTOR = '[data-customize-key]';

export const generateStableKey = (nameAttr, idAttr, placeholderAttr, labelText = '') => {
    if (idAttr && idAttr.startsWith('vs') && idAttr.includes('__')) {
        idAttr = null;
    }

    let keyStr = nameAttr || idAttr || placeholderAttr || labelText || '';
    if (!keyStr) return null;

    let words = keyStr.toString().replace(/[^a-zA-Z0-9]/g, ' ').split(/\s+/).filter(Boolean);
    
    if (words.length === 0) return null;

    return words.map(word => word.toLowerCase()).join('_');
};

const cleanLabelText = (labelNode) => {
    if (!labelNode) return '';
    return labelNode.innerText.replace(/[*:]/g, '').trim();
};

export const getLabelForInput = (input, refSlot = null, findFieldWrapperFn = null) => {
    const idAttr = input.getAttribute('id');
    const isValidId = idAttr && !(idAttr.startsWith('vs') && idAttr.includes('__'));
    const labelFor = isValidId ? (refSlot || document).querySelector(`label[for="${idAttr}"]`) : null;
    if (labelFor) return cleanLabelText(labelFor);
    
    const closestLabel = input.closest('label');
    if (closestLabel) return cleanLabelText(closestLabel);
    
    const wrapper = findFieldWrapperFn ? findFieldWrapperFn(input) : null;
    const isDifferentWrapper = wrapper && wrapper !== input;
    
    const wrapperLabel = isDifferentWrapper ? wrapper.querySelector('label') : null;
    if (wrapperLabel) return cleanLabelText(wrapperLabel);
    
    const isSpecialWrapper = isDifferentWrapper && (wrapper.classList.contains('v-select') || wrapper.classList.contains('input-group'));
    const prevWrapperSib = isSpecialWrapper ? wrapper.previousElementSibling : null;
    const isPrevWrapperLabel = prevWrapperSib && prevWrapperSib.tagName.toLowerCase() === 'label';
    if (isPrevWrapperLabel) return cleanLabelText(prevWrapperSib);
    
    const prevSib = !isDifferentWrapper ? input.previousElementSibling : null;
    const isPrevSibLabel = prevSib && prevSib.tagName.toLowerCase() === 'label';
    if (isPrevSibLabel) return cleanLabelText(prevSib);
    
    const parent = input.parentElement;
    if (!parent) return '';
    
    const parentLabel = parent.querySelector('label');
    if (!parentLabel) return '';
    
    const parentInputs = parent.querySelectorAll('input:not([type="hidden"]):not([type="button"]):not([type="submit"]), select, textarea');
    if (parentInputs.length !== 1) return '';
    
    return cleanLabelText(parentLabel);
};

const getColWrapper = (input) => {
    const col = input.closest('[class*="col-"]');
    if (!col) return null;
    
    const innerInputs = col.querySelectorAll('input:not([type="hidden"]):not([type="button"]):not([type="submit"]), select, textarea');
    if (innerInputs.length === 1) return col;
    
    return null;
}

const findFieldWrapper = (input) => {
    if (fieldCache.has(input)) return fieldCache.get(input);

    const colWrapper = getColWrapper(input);
    if (colWrapper) {
        fieldCache.set(input, colWrapper);
        return colWrapper;
    }

    const fg = input.closest('.form-group');
    if (fg) {
        fieldCache.set(input, fg);
        return fg;
    }

    const vSelect = input.closest('.v-select');
    if (vSelect) {
        fieldCache.set(input, vSelect);
        return vSelect;
    }
    
    const td = input.closest('td');
    if (td) {
        fieldCache.set(input, td);
        return td;
    }

    fieldCache.set(input, input);
    return input;
};

const hideAndRemoveRequired = (wrapper, input) => {
    wrapper.classList.add('customize-hidden');
    if (!input.hasAttribute('required')) return;
    
    input.removeAttribute('required');
    input.setAttribute('data-was-required', 'true');
}

const showAndRestoreRequired = (wrapper, input) => {
    wrapper.classList.remove('customize-hidden');
    if (!input.hasAttribute('data-was-required')) return;
    
    input.setAttribute('required', 'required');
}

const applyVisibilityRules = (containerNode) => {
    if (!containerNode) return;
    
    const config = store.getters['formFieldCustomization/getFieldsConfig'] || {};
    const inputs = containerNode.querySelectorAll('input:not([type="hidden"]):not([type="button"]):not([type="submit"]), select, textarea');
    
    inputs.forEach(input => {
        let labelText = getLabelForInput(input, containerNode, findFieldWrapper);

        const key = generateStableKey(
            input.getAttribute('name'), 
            input.getAttribute('id'), 
            input.getAttribute('placeholder'),
            labelText
        );
        
        if (!key) return;
        
        const wrapper = findFieldWrapper(input);
        if (!wrapper) return;

        const isHidden = config[key] === false;
        
        if (isHidden) {
            hideAndRemoveRequired(wrapper, input);
            return;
        }

        showAndRestoreRequired(wrapper, input);
    });

    applyCustomElementRules(containerNode, config);
};

const applyCustomElementRules = (containerNode, config) => {
    const elements = containerNode.querySelectorAll(CUSTOM_ELEMENT_SELECTOR);

    elements.forEach(element => {
        const key = element.getAttribute('data-customize-key');
        if (!key) return;

        if (config[key] === false) {
            element.classList.add('customize-hidden');
            return;
        }

        element.classList.remove('customize-hidden');
    });
};

const createObserver = (targetNode, onUpdate = null) => {
    if (!targetNode) return null;
    
    const configObj = { childList: true, subtree: true };
    const callback = (mutationsList) => {
        let shouldApply = false;
        for (const mutation of mutationsList) {
            if (mutation.type !== 'childList') continue;
            if (mutation.addedNodes.length > 0) shouldApply = true;
            if (mutation.removedNodes.length > 0) shouldApply = true;
        }
        if (!shouldApply) return;
        applyVisibilityRules(targetNode);
        if (!onUpdate) return;
        onUpdate(targetNode);
    };
    
    const observer = new MutationObserver(callback);
    observer.observe(targetNode, configObj);
    return observer;
};

const checkCustomizableFields = (node, route = null) => {
    if (!node) return false;

    const sidebars = store.getters['company/getSidebar'] || [];
    const menuId = route && route.params ? route.params.menuId : null;
    const selectedSidebar = sidebars.find(i => i.id == menuId);
    const isDashboard = selectedSidebar ? selectedSidebar.is_dashboard : false;

    if (isDashboard) return false;
    
    const inputs = node.querySelectorAll('input:not([type="hidden"]):not([type="button"]):not([type="submit"]), select, textarea');
    if (inputs.length === 0) return false;

    return true;
};

export default function useFormVisibilityEngine() {
    return {
        applyVisibilityRules,
        createObserver,
        findFieldWrapper,
        generateStableKey,
        getLabelForInput,
        checkCustomizableFields,
        CUSTOM_ELEMENT_SELECTOR
    };
}
